import { useBranding } from '@/lib/branding';
import { useTheme } from '@/lib/theme';
import { cn } from '@/lib/utils';

type Size = 'sm' | 'md' | 'lg' | 'xl';
const IMG_H: Record<Size, string> = { sm: 'h-5', md: 'h-7', lg: 'h-9', xl: 'h-11' };
const TEXT: Record<Size, string> = { sm: 'text-base', md: 'text-xl', lg: 'text-2xl', xl: 'text-4xl' };

/**
 * Tenant logo with a text wordmark fallback.
 *
 * `surface` forces the variant for a known background (e.g. the sign-in brand panel is always dark);
 * otherwise it follows the active theme. When a tenant has not supplied a dedicated dark logo, the
 * light logo is rendered in white on dark surfaces so single-colour wordmarks stay legible.
 */
export function Logo({ className, size = 'md', surface }: { className?: string; size?: Size; surface?: 'light' | 'dark' }) {
  const { branding } = useBranding();
  const { resolved } = useTheme();
  const onDark = (surface ?? resolved) === 'dark';
  const hasDarkLogo = !!branding.logoDarkUrl && branding.logoDarkUrl !== branding.logoUrl;
  const src = onDark ? (branding.logoDarkUrl ?? branding.logoUrl) : branding.logoUrl;
  const whiten = onDark && !hasDarkLogo;

  if (src) {
    return <img src={src} alt={branding.appName} className={cn(IMG_H[size], 'w-auto max-w-[200px] object-contain', whiten && 'brightness-0 invert', className)} />;
  }
  return <span className={cn('font-display leading-none tracking-tight', TEXT[size], className)}>{branding.appName}</span>;
}
